import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { buyerService } from "../../services/enhancedCrudService";
import { toast } from "react-toastify";

const BuyerList = () => {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 10,
  });

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    sort_by: "created_at",
    sort_order: "desc",
  });

  const fetchBuyers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...filters,
      };
      const response = await buyerService.getInquiryList(params);
      console.log("Buyers response:", response);
      setBuyers(response.data || response.results || []);
      setPagination((prev) => ({
        ...prev,
        current_page: response.current_page || response.page || 1,
        total_pages: response.total_pages || 1,
        total_records: response.total_records || response.total || 0,
      }));
    } catch (error) {
      console.error(
        "Error fetching buyers:",
        error.response?.data || error.message
      );
      toast.error(error.response?.data?.detail || "Failed to load buyers");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPagination((prev) => ({
      ...prev,
      current_page: 1,
    }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      current_page: page,
    }));
  };

  const handleStatusChange = async (buyerId, newStatus) => {
    try {
      toast.success("Status updated successfully");
      fetchBuyers();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (buyerId) => {
    if (window.confirm("Are you sure you want to delete this buyer?")) {
      try {
        toast.success("Buyer deleted successfully");
        fetchBuyers();
      } catch (error) {
        console.error("Error deleting buyer:", error);
        toast.error("Failed to delete buyer");
      }
    }
  };

  const renderPagination = () => {
    const pages = [];
    const { current_page, total_pages } = pagination;

    for (let i = 1; i <= total_pages; i++) {
      if (
        i === 1 ||
        i === total_pages ||
        (i >= current_page - 2 && i <= current_page + 2)
      ) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...");
      }
    }

    return (
      <nav>
        <ul className="pagination justify-content-center">
          <li className={`page-item ${current_page === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(current_page - 1)}
              disabled={current_page === 1}
            >
              Previous
            </button>
          </li>

          {pages.map((page, index) => (
            <li
              key={index}
              className={`page-item ${page === current_page ? "active" : ""} ${
                page === "..." ? "disabled" : ""
              }`}
            >
              {page === "..." ? (
                <span className="page-link">...</span>
              ) : (
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              )}
            </li>
          ))}

          <li
            className={`page-item ${
              current_page === total_pages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(current_page + 1)}
              disabled={current_page === total_pages}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  useEffect(() => {
    fetchBuyers();
  }, [pagination.current_page, filters]);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h4 className="card-title">Buyers Management</h4>
            <div className="card-actions">
              <Link to="/buyers/add" className="btn btn-primary">
                Add New Buyer
              </Link>
            </div>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search buyers..."
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Sr.No</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {buyers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No buyers found
                      </td>
                    </tr>
                  ) : (
                    buyers.map((buyer,index) => (
                      <tr key={index}>
                      <td>{index + 1}</td>
                        <td>{buyer.name || "N/A"}</td>
                        <td>{buyer.email || "N/A"}</td>
                        <td>{buyer.phone || "N/A"}</td>
                        <td>
                          <span
                            className={`badge badge-${
                              buyer.status === "active"
                                ? "success"
                                : buyer.status === "inactive"
                                ? "danger"
                                : "warning"
                            }`}
                          >
                            {buyer.status || "pending"}
                          </span>
                        </td>
                        <td>
                          {buyer.created_at
                            ? new Date(buyer.created_at).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td>
                          <div className="btn-group" role="group">
                            <Link
                              to={`/buyers/profile/${buyer.id}`}
                              className="btn btn-sm btn-info"
                            >
                              View
                            </Link>
                            <Link
                              to={`/buyers/edit/${buyer.id}`}
                              className="btn btn-sm btn-primary"
                            >
                              Edit
                            </Link>
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() =>
                                handleStatusChange(
                                  buyer.id,
                                  buyer.status === "active"
                                    ? "inactive"
                                    : "active"
                                )
                              }
                            >
                              {buyer.status === "active"
                                ? "Deactivate"
                                : "Activate"}
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(buyer.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {pagination.total_pages > 1 && (
              <div className="mt-3">
                {renderPagination()}
                <div className="text-center mt-2">
                  <small className="text-muted">
                    Showing{" "}
                    {(pagination.current_page - 1) * pagination.per_page + 1} to{" "}
                    {Math.min(
                      pagination.current_page * pagination.per_page,
                      pagination.total_records
                    )}{" "}
                    of {pagination.total_records} entries
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerList;
